/**
 * Geometrie fuer StaticDailyRewardsChart (F-R3-2).
 *
 * Ausgelagert, damit die Komponentendatei ausschliesslich Komponenten
 * exportiert (react-refresh/only-export-components).
 */
import type { DailyReward } from './useMonthlyReport'

/** Feste Zeichenflaeche — bewusst KEINE Prozentbreite, damit das Layout beim
 * Print-Reflow nicht neu gemessen werden muss. */
export const VIEW_W = 720
export const VIEW_H = 220
export const PAD_L = 52
export const PAD_R = 12
const PAD_T = 10
const PAD_B = 26

const PLOT_W = VIEW_W - PAD_L - PAD_R
const PLOT_H = VIEW_H - PAD_T - PAD_B

/** '2026-05-10' -> '10.05' */
function shortDate(dateStr: string): string {
  const parts = dateStr.split('-')
  if (parts.length !== 3) return dateStr
  return `${parts[2]}.${parts[1]}`
}

type Geometry = {
  points: string
  yTicks: { y: number; label: string }[]
  xTicks: { x: number; label: string }[]
}

export function buildStaticChartGeometry(series: DailyReward[]): Geometry {
  const values = series.map((d) => d.rewards_hnt ?? 0)
  const rawMax = values.length > 0 ? Math.max(...values) : 0
  // Ein flacher Nullverlauf wuerde sonst durch 0 teilen.
  const max = rawMax > 0 ? rawMax : 1
  const lastIndex = Math.max(series.length - 1, 1)

  const xAt = (index: number) => PAD_L + (index / lastIndex) * PLOT_W
  const yAt = (value: number) => PAD_T + (1 - value / max) * PLOT_H

  const points = series
    .map((d, i) => `${xAt(i).toFixed(2)},${yAt(d.rewards_hnt ?? 0).toFixed(2)}`)
    .join(' ')

  const yTicks = [0, 0.5, 1].map((frac) => ({
    y: PAD_T + (1 - frac) * PLOT_H,
    label: (max * frac).toFixed(2),
  }))

  // Hoechstens 8 X-Labels, sonst ueberlappen die Tage im Druck.
  const step = Math.max(1, Math.ceil(series.length / 8))
  const xTicks = series
    .map((d, i) => ({ index: i, date: d.date }))
    .filter((entry) => entry.index % step === 0)
    .map((entry) => ({ x: xAt(entry.index), label: shortDate(entry.date) }))

  return { points, yTicks, xTicks }
}
